import { useCallback, useMemo, useState } from 'react';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import { Row, Col, Button } from 'antd';

import Flat from '../../../backend/s3/flat';
import { getNearFlats } from '../../../backend/recommendations';
import Office from '../../../backend/s3/office';
import Employee from '../../../backend/s3/employee';
import { SEARCH_BY_COUNTY_KEYWORD, SITE_URL } from '../../../common/consts';
import { IContact } from '../../../common/model/mailchimp/contact.model';
import { IFlatWithOffice } from '../../../common/model/flat.model';
import { IOffice } from '../../../common/model/office.model';
import { MailchimpInterest } from '../../../common/model/mailchimp/enums/mailchimpInterest.enum';
import {
  getFlatsWithOffice,
  getOfficesWithEmployees,
} from '../../../common/queries';
import useI18n from '../../../common/hooks/useI18n';
import useMailchimpService from '../../../common/hooks/mailchimpService';
import { getFlatSlug } from '../../../common/helpers/flat.utils';
import * as flatTypeUtils from '../../../common/helpers/flatType.utils';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../../../common/helpers/buyHouseOption.utils';
import {
  deserializeMultiple,
  deserializeSingle,
} from '../../../common/helpers/serialization';
import { generateFlatDetailPageStructuredData } from '../../../common/helpers/structuredData.utils';

import {
  FlatImageCarousel,
  Description,
  FeaturesCard,
  Location,
  OfficeInfoCard,
  RequestInfoButton,
} from '../../../components/flatDetail';
import {
  Header,
  Footer,
  FlatsDisplay,
  Gallery,
  Head,
  SimpleMap,
  FlatBreadcrumb,
} from '../../../components/shared';

interface StaticProps {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
  flat: string;
  recommendedFlats: string;
}

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const BreadcrumbContainer = styled.div`
  margin-bottom: 24px;

  @media ${(props) => props.theme.breakpoints.smd} {
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const DescriptionSection = styled.div`
  margin-top: 2rem;
  margin-left: calc(
    max(${(props) => props.theme.grid.getGridColumns(2, 1)}, 40px)
  );
  margin-right: calc(
    max(${(props) => props.theme.grid.getGridColumns(2, 1)}, 40px)
  );
`;

const VirtualVisitButton = styled(Button)`
  margin-top: 16px;
  margin-left: -15px;
  white-space: normal;
`;

const MapSection = styled.div`
  margin-top: 24px;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 16px;
  }
`;

const FeaturesCardContainer = styled.div`
  margin-top: 0;

  @media ${(props) => props.theme.breakpoints.lgu} {
    margin-top: 50px;
  }
`;

const FeaturesTitle = styled.h3`
  ${(props) => props.theme.font.h2}
  margin-bottom: 16px;
`;

const ContactTitle = styled.h3`
  ${(props) => props.theme.font.h2}
  margin-bottom: 16px;
  margin-top: 3rem;
`;

const RequestInfoSection = styled.div`
  margin-top: 2rem;
`;

const FlatDetailPage = ({
  offices,
  buyHouseOptions,
  flat,
  recommendedFlats,
}: Props): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();
  const mailchimpService = useMailchimpService();

  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [carouselCurrentIndex, setCarouselCurrentIndex] = useState(0);

  const deserializedFlat = useMemo(
    () => deserializeSingle<IFlatWithOffice>(flat),
    [flat]
  );
  const deserializedRecommendedFlats = useMemo(
    () => deserializeMultiple<IFlatWithOffice>(recommendedFlats),
    [recommendedFlats]
  );

  const canonicalUrl = useMemo(
    () =>
      `${SITE_URL}/comprar/${flatTypeUtils.getFlatTypeSlug(
        deserializedFlat.type
      )}/${getFlatSlug(deserializedFlat)}`,
    [deserializedFlat]
  );

  const structuredData = useMemo(
    () => generateFlatDetailPageStructuredData(deserializedFlat, canonicalUrl),
    [deserializedFlat, canonicalUrl]
  );

  const title = useMemo(
    () =>
      i18n.t('flat.title', {
        type: i18n.t(flatTypeUtils.getFlatTypeLabel(deserializedFlat.type)),
        address: deserializedFlat.address,
      }),
    [deserializedFlat.address, deserializedFlat.type, i18n]
  );

  const description = useMemo(
    () =>
      i18n.t('flatDetail.description', {
        type: deserializedFlat.type,
        zone: deserializedFlat.zone,
        city: deserializedFlat.city,
        sqrMeters: deserializedFlat.sqrMeters,
        rooms: deserializedFlat.rooms,
        bathrooms: deserializedFlat.bathrooms,
        price: deserializedFlat.price,
      }),
    [
      deserializedFlat.bathrooms,
      deserializedFlat.city,
      deserializedFlat.price,
      deserializedFlat.rooms,
      deserializedFlat.sqrMeters,
      deserializedFlat.type,
      deserializedFlat.zone,
      i18n,
    ]
  );

  const onShowVirtualVisitButtonClick = useCallback(() => {
    const handleShowVirtualVisitButtonClick = () => {
      setCarouselCurrentIndex(deserializedFlat.pictureUrls.length);
      setIsGalleryVisible(true);
    };

    handleShowVirtualVisitButtonClick();
  }, [deserializedFlat.pictureUrls.length]);

  const onShowAllPhotosButtonClick = useCallback(() => {
    setIsGalleryVisible(true);
  }, []);

  const onBuyButtonClick = useCallback(
    (contact: IContact) => {
      const handleBuyButtonClick = (contact: IContact) => {
        contact.DFLAT = `${deserializedFlat.address} - ${deserializedFlat.zone} - ${deserializedFlat.city}`;
        if (deserializedFlat.office) {
          contact.officeEmail = deserializedFlat.office.email;
        }
        mailchimpService.subscribe(
          { ...contact, interests: [MailchimpInterest.COMPRAR] },
          router,
          i18n
        );
      };

      handleBuyButtonClick(contact);
    },
    [
      deserializedFlat.address,
      deserializedFlat.city,
      deserializedFlat.office,
      deserializedFlat.zone,
      i18n,
      mailchimpService,
      router,
    ]
  );

  const onGalleryCancel = useCallback(() => {
    setIsGalleryVisible(false);
    setCarouselCurrentIndex(0);
  }, []);

  return (
    <Layout>
      <Head title={title} description={description} canonicalUrl={canonicalUrl}>
        {structuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        ) : null}
      </Head>

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <FlatImageCarousel
          flat={deserializedFlat}
          onShowVirtualVisitButtonClick={onShowVirtualVisitButtonClick}
          onShowAllPhotosButtonClick={onShowAllPhotosButtonClick}
        />
        <DescriptionSection>
          <BreadcrumbContainer>
            <FlatBreadcrumb
              searchOptions={[]}
              county={deserializedFlat.county + ' ' + SEARCH_BY_COUNTY_KEYWORD}
              city={deserializedFlat.city}
              zone={deserializedFlat.zone}
              flatTitle={title}
              flatType={flatTypeUtils.getFlatTypeSlug(
                deserializedFlat.type,
                true
              )}
            />
          </BreadcrumbContainer>

          <Row gutter={[80, 48]}>
            <Col xs={24} lg={14} xl={15}>
              <Description flat={deserializedFlat} title={title} />
              {deserializedFlat.matterportUrl && (
                <VirtualVisitButton
                  type="link"
                  onClick={onShowVirtualVisitButtonClick}
                >
                  {i18n.t('flatDetail.actions.virtualVisitLong')}
                </VirtualVisitButton>
              )}

              {typeof google !== 'undefined' &&
                typeof google.maps.Map !== 'undefined' && (
                  <MapSection>
                    <Location flat={deserializedFlat} />
                    <br />
                    <SimpleMap
                      latLongArray={[
                        {
                          key: deserializedFlat.id,
                          lat: deserializedFlat.approximateLatitude,
                          long: deserializedFlat.approximateLongitude,
                        },
                      ]}
                    />
                  </MapSection>
                )}
            </Col>

            <Col xs={24} lg={10} xl={9}>
              <FeaturesCardContainer>
                <FeaturesTitle>
                  {i18n.t('flatDetail.messages.featuresTitle')}
                </FeaturesTitle>
                <FeaturesCard flat={deserializedFlat} />
                {deserializedFlat.office && (
                  <>
                    <ContactTitle>
                      {i18n.t('flatDetail.messages.contactTitle')}
                    </ContactTitle>
                    <OfficeInfoCard flat={deserializedFlat} />
                  </>
                )}
                <RequestInfoSection>
                  <Row>
                    <Col
                      xs={{ span: 24, offset: 0 }}
                      sm={{ span: 18, offset: 3 }}
                      md={{ span: 12, offset: 6 }}
                      lg={{ span: 24, offset: 0 }}
                    >
                      <RequestInfoButton onButtonClick={onBuyButtonClick} />
                    </Col>
                  </Row>
                </RequestInfoSection>
              </FeaturesCardContainer>
            </Col>
          </Row>
        </DescriptionSection>

        <FlatsDisplay
          flats={deserializedRecommendedFlats}
          offices={offices}
          title={i18n.t('flatDetail.messages.recommendedFlats')}
        />

        <Gallery
          pictureUrls={deserializedFlat.pictureUrls}
          visible={isGalleryVisible}
          carouselCurrentIndex={carouselCurrentIndex}
          onCarouselCurrentIndexChange={setCarouselCurrentIndex}
          onCancel={onGalleryCancel}
          matterportUrl={deserializedFlat.matterportUrl}
        />
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const [flats, _] = await Flat.getFlats();

  const paths = flats.map((flat) => ({
    params: {
      flatType: flatTypeUtils.getFlatTypeSlug(flat.type),
      slug: getFlatSlug(flat),
    },
  }));

  return { paths, fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<StaticProps> = async ({
  params,
}) => {
  const [flats, searchOptions] = await Flat.getFlats();

  const flatIndex = flats.findIndex((f) => getFlatSlug(f) === params.slug);
  if (flatIndex < 0) {
    return {
      notFound: true,
    };
  }

  const flat = flats[flatIndex];
  if (
    params.flatType !== flatTypeUtils.getFlatTypeSlug(flat.type) &&
    params.flatType !== 'vivienda'
  ) {
    return {
      notFound: true,
    };
  }

  if (params.flatType === 'vivienda') {
    return {
      redirect: {
        destination: `/comprar/${flatTypeUtils.getFlatTypeSlug(flat.type)}/${
          params.slug
        }`,
        statusCode: 301,
      },
    };
  }

  const offices = await Office.getOffices();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  const employees = await Employee.getEmployees();
  const officesWithEmployees = getOfficesWithEmployees(offices, employees);
  const flatsWithOffice = getFlatsWithOffice(flats, officesWithEmployees);
  const flatWithOffice = flatsWithOffice[flatIndex];
  const recommendedFlats = getNearFlats(flatsWithOffice, flatIndex, 4);

  const serializedFlat = JSON.stringify(flatWithOffice);
  const serializedRecommendedFlats = JSON.stringify(recommendedFlats);

  return {
    props: {
      offices,
      buyHouseOptions,
      flat: serializedFlat,
      recommendedFlats: serializedRecommendedFlats,
    },
  };
};

export default FlatDetailPage;
